// Centralized color tokens (prefer CSS vars; fall back to safe defaults)
const COLOR_PRIMARY = 'var(--color-primary, #f97316)';
const COLOR_PRIMARY_RGB = 'var(--color-primary-rgb, 249 115 22)';
const COLOR_SUCCESS = 'var(--color-success, #22c55e)';
const COLOR_SUCCESS_RGB = 'var(--color-success-rgb, 34 197 94)';
const COLOR_ERROR = 'var(--color-error, #ef4444)';
const COLOR_ERROR_RGB = 'var(--color-error-rgb, 239 68 68)';
const COLOR_BORDER_RGB = 'var(--color-border-rgb, 209 213 219)';
const COLOR_FOREGROUND_RGB = 'var(--color-foreground-rgb)';

// Derived tokens
const DISABLED_BG = `rgb(${COLOR_BORDER_RGB})`;
const DISABLED_TEXT = `rgb(${COLOR_FOREGROUND_RGB} / 50%)`;
const NEUTRAL_BG_06 = `rgb(${COLOR_FOREGROUND_RGB} / 0.06)`;
const NEUTRAL_TEXT_80 = `rgb(${COLOR_FOREGROUND_RGB} / 80%)`;
const SUCCESS_BG_10 = `rgb(${COLOR_SUCCESS_RGB} / 10%)`;
const SUCCESS_BG_20 = `rgb(${COLOR_SUCCESS_RGB} / 20%)`;
const SUCCESS_BORDER_60 = `rgb(${COLOR_SUCCESS_RGB} / 60%)`;
const ERROR_BG_10 = `rgb(${COLOR_ERROR_RGB} / 10%)`;
const ERROR_BG_20 = `rgb(${COLOR_ERROR_RGB} / 20%)`;
const ERROR_BORDER_60 = `rgb(${COLOR_ERROR_RGB} / 60%)`;

export default class PackBuilderComponent extends HTMLElement {
  constructor() {
    super();
    this.currentStep = 1;
    this.selectedPackSize = 6;
    this.selectedProducts = new Map();
    this.selectedFrequency = 0;
    this.maxItems = 6;
    this.packPrices = {
      6: parseFloat(this.dataset.pack6Price) || 71.94,
      9: parseFloat(this.dataset.pack9Price) || 95.94
    };
    this.deliveryZones = this.dataset.deliveryZones?.split('|') || [];
    this.parentId = this.querySelector('.bundle-parent__product').dataset.productId;

    this.storageKey = 'pack-builder-progress';
    this.isInfluencer = false;
    this.loading = true;
    this.loadProgress();
    this.checkUrl();
  }

  connectedCallback() {
    if (!this.loading) {
      this.initializeElements();
      this.attachEventListeners();
      this.updatePackPreviewGrid();
      this.restoreVisualState();
      this.updateDisplay();

      this.addEventListener('keydown', this.handleKeyboardNavigation.bind(this));

      this.querySelectorAll('.pack-option, .pack-item, .frequency-option').forEach(element => {
        if (!element.hasAttribute('tabindex')) {
          element.setAttribute('tabindex', '0');
        }
      });
    }
  }

  async checkUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    const influence_id = urlParams.get('influencerId');
    const lockedProducts = urlParams.get('products')?.split(',');
    if (!lockedProducts) {
      this.loading = false;
      this.loadProgress();
      this.showNotifications();
      return;
    };
    const defaultPacksize = [6, 9];
    const savedProducts = new Map();
    const isInfluencer = defaultPacksize.filter(item => item === lockedProducts?.length).length !== 0;
    this.isInfluencer = isInfluencer ? influence_id : false;
    
    try {
      for (let i = 0; i < lockedProducts.length; i++) {
        const response = await fetch(`https://quickgreens.myshopify.com/products/${lockedProducts[i]}.js`);
        const res = await response.json();
        const firstVariantId = String(res.variants[0].id);
        const selling_plans = res.selling_plan_groups.map(item => item.selling_plans[0].id);
        if (savedProducts.get(firstVariantId)) {
          savedProducts.set(firstVariantId, [savedProducts.get(firstVariantId)[0] + 1, selling_plans]);
        } else {
          savedProducts.set(firstVariantId, [1, selling_plans]);
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      this.loading = false;
      this.loadProgress(Array.from(savedProducts.entries()), defaultPacksize.find(item => item >= lockedProducts.length));
      this.connectedCallback();
    }
  }

  influenceDisable() {
    const packItems = this.querySelectorAll('.pack-item');
    packItems.forEach(item => {
      item.style.pointerEvents = 'none';
      item.style.opacity = '0.7';
    });
    const removeBtn = this.querySelectorAll('.remove-product-btn');
    removeBtn.forEach(item => {
      item.style.display = 'none';
    });
    const decreaseBtn = this.querySelectorAll('.quantity-decrease');
    decreaseBtn.forEach(item => {
      item.style.pointerEvents = 'none';
      item.style.opacity = '0.5';
    });
    this.querySelector('.pack-size-selector').style.pointerEvents = 'none';
    this.querySelector('.pack-size-selector').style.opacity = '0.7';
  }

  initializeElements() {
    this.progressBar = this.querySelector('.pack-progress-fill');
    this.stepIndicators = this.querySelectorAll('.step-indicator');
    this.packSizeOptions = this.querySelectorAll('.pack-option');
    this.productGrid = this.querySelector('.product-selection-grid');
    this.frequencyOptions = this.querySelectorAll('.frequency-option');
    this.packSummary = this.querySelector('.pack-summary-content');
    this.addToCartBtn = this.querySelector('.add-pack-to-cart');
    this.selectedCountDisplay = this.querySelector('.selected-count');
    this.maxItemsDisplay = this.querySelector('.max-items');
    this.totalPriceDisplay = this.querySelector('.total-price');
    this.dietaryFilters = this.querySelectorAll('.dietary-filter');
    this.zipCodeInput = this.querySelector('.zip-code-input');
    this.deliveryStatus = this.querySelector('.delivery-status');
    this.packModal = document.querySelector('.unified-modal__wrapper');
    
    // Wait for unified modal to be available
    this.waitForUnifiedModal();
  }

  waitForUnifiedModal() {
    const checkModal = () => {
      if (window.unifiedProductModal) {
        this.unifiedModal = window.unifiedProductModal;
        // console.log('Unified modal found and connected to pack builder');
      } else {
        setTimeout(checkModal, 100);
      }
    };
    checkModal();
  }

  attachEventListeners() {
    this.packSizeOptions?.forEach(option => {
      option.addEventListener('click', () => this.selectPackSize(option));
    });

    this.packSizeSelector = this.querySelector('.pack-size-selector');
    this.packSizeSelector?.addEventListener('change', (e) => this.handlePackSizeChange(e.target.value));

    this.deliveryFrequencySelector = this.querySelector('.delivery-frequency-selector');
    this.deliveryFrequencySelector?.addEventListener('change', (e) => this.handleFrequencyChange(e.target.value));

    this.productGrid?.addEventListener('click', (e) => {
      const productItem = e.target.closest('.pack-item');
      if (productItem) {
        this.handleProductSelection(productItem, e);
      }
    });

    // Handle unified modal events for pack builder functionality
    document.addEventListener('click', (e) => {
      // Only handle events within the unified modal when it's in pack mode
      if (!this.packModal?.classList.contains('show')) return;
      if (!this.unifiedModal || this.unifiedModal.mode !== 'pack') return;
      
      const path = (e.composedPath && e.composedPath()) || [];
      const baseTarget = (e.target && e.target.nodeType === 1 ? e.target : path.find((n) => n && n.nodeType === 1)) || e.currentTarget;
      
      const productItem = baseTarget && baseTarget.closest ? baseTarget.closest('.pack-add-btn') : null;
      if (productItem) {
        // console.log('Pack add button clicked in modal');
        this.handleProductSelection(productItem, e);
        return;
      }
      
      const quantityBtn = baseTarget && baseTarget.closest ? baseTarget.closest('.quantity-btn') : null;
      if (quantityBtn) {
        // console.log('Quantity button clicked in modal:', quantityBtn.className);
        const productId = this.packModal?.querySelector('.pack-add-btn')?.dataset.productId;
        if (productId) {
          this.handleQuantityChange(productId, quantityBtn);
          // Update modal quantity controls after change
          this.updateModalQuantityControls(productId);
        }
        return;
      }
    });

    this.addEventListener('click', (e) => {
      const removeBtn = e.target.closest('.remove-product-btn');
      if (removeBtn) {
        const productId = removeBtn.dataset.productId;
        this.removeProductFromPack(productId);
      }
    });

    this.frequencyOptions?.forEach(option => {
      option.addEventListener('click', () => this.selectFrequency(option));
    });

    this.dietaryFilters?.forEach(filter => {
      filter.addEventListener('click', () => this.toggleDietaryFilter(filter));
    });

    this.addToCartBtn?.addEventListener('click', () => this.addPackToCart());

    this.zipCodeInput?.addEventListener('input', () => this.validateDeliveryZone());
    this.zipCodeInput?.addEventListener('blur', () => this.validateDeliveryZone());

    this.querySelector('.next-step')?.addEventListener('click', () => this.nextStep());
    this.querySelector('.prev-step')?.addEventListener('click', () => this.prevStep());

    this.addEventListener('change', () => {
      this.saveProgress();
    });

    // Modal close events are now handled by the unified modal itself

    const originalUpdateDisplay = this.updateDisplay.bind(this);
    this.updateDisplay = function () {
      originalUpdateDisplay();
      this.saveProgress();
    };
  }

  selectPackSize(option) {
    this.packSizeOptions.forEach(opt => opt.classList.remove('selected'));
    option.classList.add('selected');

    this.selectedPackSize = parseInt(option.dataset.size);
    this.maxItems = this.selectedPackSize;

    if (this.getTotalSelectedItems() > this.maxItems) {
      this.clearExcessProducts();
    }

    this.updateDisplay();
    this.nextStep();
  }

  handleProductSelection(productItem, event) {
    const productId = productItem.dataset.productId;
    const selling_plane = productItem.dataset.productSelling?.split(",") || [];

    const isQuantityControl = event.target.closest('.quantity-controls');
    const isAddButton = event.target.closest('.pack-add-btn');
    const openModal = event.target.closest('.pack-item-image') || event.target.closest('.pack-item-title');

    if (isQuantityControl) {
      this.handleQuantityChange(productId, event.target);
    } else if (isAddButton) {
      this.addProductToPack(productId);
    } else if (openModal) {
      this.showPackModal(productItem);
    }
  }

  showPackModal(productItem) {
    const productId = productItem.dataset.productId;
    const productHandle = productItem.dataset.productHandle;
    const productTitle = productItem.dataset.productTitle;
    const productImage = productItem.dataset.productImage;
    const productSelling = productItem.dataset.productSelling;
    const productPrice = productItem.dataset.productPrice;
    const productDescription = productItem.dataset.productDescription;
    const inventory = this.getProductInventory(productId);
    
    // Minimal data - let Section Rendering handle the rest
    const productData = {
      productId: productId,
      handle: productHandle,
      title: productTitle,
      image: productImage,
      description: productDescription,
      price: productPrice,
      sellingPlans: productSelling,
      available: inventory.available
    };
    
    // Use the unified modal in pack mode - it will fetch metafields via Section Rendering
    if (window.unifiedProductModal) {
      window.unifiedProductModal.show(productData, 'pack');
      
      // Set up pack-specific functionality after modal is shown
      setTimeout(() => {
        const addPackBtn = document.querySelector('.pack-add-btn');
        if (addPackBtn) {
          if (!inventory.available) {
            addPackBtn.textContent = 'Sold Out';
            addPackBtn.disabled = true;
            addPackBtn.style.background = DISABLED_BG;
            addPackBtn.style.color = DISABLED_TEXT;
          } else {
            addPackBtn.textContent = `Add • ${productPrice}`;
            addPackBtn.disabled = false;
            addPackBtn.style.background = COLOR_PRIMARY;
            addPackBtn.style.color = 'white';
          }
        }
        
        this.updateProductDisplay(productId);
        this.updateModalQuantityControls(productId);
      }, 10); // Small delay to ensure modal is fully rendered
    }
  }

  addProductToPack(productId) {
    const inventory = this.getProductInventory(productId);
    
    if (!inventory.available) {
      this.showNotification('This product is currently sold out.', 'error');
      return;
    }

    const totalSelected = this.getTotalSelectedItems();

    if (totalSelected >= this.maxItems) {
      this.showMaxItemsMessage();
      return;
    }

    // Get selling plans from product data attributes
    const productElement = this.querySelector(`[data-product-id="${productId}"]`);
    const selling_plane = productElement?.dataset.productSelling?.split(",") || [];

    this.selectedProducts.set(productId, [1, selling_plane]);
    this.updateProductDisplay(productId);
    this.updateDisplay();

    if (this.getTotalSelectedItems() === this.maxItems) {
      setTimeout(() => this.nextStep(), 500);
    }
  }

  handlePackSizeChange(newSize) {
    const oldSize = this.selectedPackSize;
    this.selectedPackSize = parseInt(newSize);
    this.maxItems = this.selectedPackSize;

    this.updatePackPreviewGrid();

    if (this.getTotalSelectedItems() > this.maxItems) {
      this.clearExcessProducts();
    }

    this.updateDisplay();

    this.saveProgress();
  }

  handleFrequencyChange(newFrequency) {
    this.selectedFrequency = newFrequency;
    this.updateDisplay();
  }

  updatePackPreviewGrid() {
    const previewGrid = this.querySelector('.pack-preview-grid');
    if (!previewGrid) return;

    previewGrid.innerHTML = '';
    previewGrid.setAttribute('data-pack-size', this.selectedPackSize);

    for (let i = 0; i < this.selectedPackSize; i++) {
      const slot = document.createElement('div');
      slot.className = 'pack-preview-slot';
      slot.setAttribute('data-slot-index', i);
      slot.innerHTML = '<span class="plus-icon">+</span>';
      previewGrid.appendChild(slot);
    }

    if (this.selectedPackSize === 9) {
      previewGrid.style.gridTemplateColumns = 'repeat(3, 1fr)';
    } else {
      previewGrid.style.gridTemplateColumns = 'repeat(3, 1fr)';
    }

    this.updatePackPreview();
  }

  updatePackPreview() {
    const previewSlots = this.querySelectorAll('.pack-preview-slot');
    if (!previewSlots.length) return;

    previewSlots.forEach(slot => {
      slot.classList.remove('filled');
      slot.innerHTML = '<span class="plus-icon">+</span>';
    });

    let slotIndex = 0;
    for (const [productId, [quantity, selling_plane]] of this.selectedProducts.entries()) {
      const productData = this.getProductData(productId);
      if (!productData) continue;

      for (let q = 0; q < quantity; q++) {
        if (slotIndex >= previewSlots.length) break;

        const slot = previewSlots[slotIndex];
        slot.classList.add('filled');
        slot.innerHTML = `
          <img src="${productData.image}" alt="${productData.title}" />
          <button class="remove-product-btn" data-product-id="${productId}" data-product-selling="${selling_plane}" aria-label="Remove one ${productData.title}">
            ×
          </button>
        `;
        slotIndex++;
      }
    }
  }

  removeProductFromPack(productId) {
    const currentData = this.selectedProducts.get(productId);
    if (!currentData) return;
    
    const [currentQuantity, selling_plane] = currentData;

    if (currentQuantity > 1) {
      this.selectedProducts.set(productId, [currentQuantity - 1, selling_plane]);
    } else if (currentQuantity === 1) {
      this.selectedProducts.delete(productId);
    }

    this.updateProductDisplay(productId);
    this.updateDisplay();
  }

  toggleProductSelection(productId) {
    const currentData = this.selectedProducts.get(productId);
    const currentQuantity = currentData ? currentData[0] : 0;
    const totalSelected = this.getTotalSelectedItems();

    if (currentQuantity === 0 && totalSelected < this.maxItems) {
      // Get selling plans from product data attributes
      const productElement = this.querySelector(`[data-product-id="${productId}"]`);
      const selling_plane = productElement?.dataset.productSelling?.split(",") || [];
      this.selectedProducts.set(productId, [1, selling_plane]);
    } else if (currentQuantity > 0) {
      this.selectedProducts.delete(productId);
    } else {
      this.showMaxItemsMessage();
      return;
    }

    this.updateProductDisplay(productId);
    this.updateDisplay();
  }

  handleQuantityChange(productId, button) {
    // console.log('handleQuantityChange called:', { productId, button: button.className });
    const currentData = this.selectedProducts.get(productId);
    if (!currentData) return;
    
    const [currentQuantity, selling_plane] = currentData;
    const isIncrease = button.classList.contains('quantity-increase');
    const isDecrease = button.classList.contains('quantity-decrease');
    const totalSelected = this.getTotalSelectedItems();
    const inventory = this.getProductInventory(productId);
    
    // console.log('Quantity change details:', { currentQuantity, isIncrease, isDecrease, totalSelected, maxItems: this.maxItems });

    if (isIncrease && totalSelected < this.maxItems) {
      // Check if we can add more based on inventory (only if inventory is tracked)
      if (inventory.isInventoryTracked && currentQuantity >= inventory.quantity) {
        this.showNotification(`Only ${inventory.quantity} items available in stock.`, 'warning');
        return;
      }
      this.selectedProducts.set(productId, [currentQuantity + 1, selling_plane || []]);
    } else if (!isIncrease && isDecrease && currentQuantity > 1) {
      this.selectedProducts.set(productId, [currentQuantity - 1, selling_plane || []]);
    } else if (!isIncrease && isDecrease && currentQuantity === 1) {
      this.selectedProducts.delete(productId);
    } else if (isIncrease) {
      this.showMaxItemsMessage();
      return;
    }

    this.updateProductDisplay(productId);
    this.updateDisplay();
  }

  updateProductDisplay(productId) {
    // console.log('updateProductDisplay called for productId:', productId);
    const productItem = this.querySelector(`[data-product-id="${productId}"]`);
    if (!productItem) {
      // console.log('Product item not found for productId:', productId);
      return;
    }

    const quantity = this.selectedProducts.get(productId) ? this.selectedProducts.get(productId)[0] : 0;
    // console.log('Updating product display with quantity:', quantity);
    const addBtn = productItem.querySelector('.pack-add-btn');
    const quantityControls = productItem.querySelector('.quantity-controls');
    const quantityDisplay = productItem.querySelector('.quantity-display');

    if (quantity > 0) {
      productItem.classList.add('selected');
      if (addBtn) addBtn.style.display = 'none';
      if (quantityControls) quantityControls.style.display = 'flex';
      if (quantityDisplay) quantityDisplay.textContent = quantity;
      // Update unified modal quantity controls
      if (window.unifiedProductModal && this.packModal?.classList.contains('show')) {
        window.unifiedProductModal.updateQuantityControls(productId, quantity);
      }
    } else {
      productItem.classList.remove('selected');
      if (addBtn) addBtn.style.display = 'block';
      if (quantityControls) quantityControls.style.display = 'none';
      // Update unified modal quantity controls
      if (window.unifiedProductModal && this.packModal?.classList.contains('show')) {
        window.unifiedProductModal.updateQuantityControls(productId, quantity);
      }
    }
    this.updateAllAddButtons();
  }

  updateModalQuantityControls(productId) {
    if (!this.packModal) return;
    
    const inventory = this.getProductInventory(productId);
    const currentQuantity = this.selectedProducts.get(productId)?.[0] || 0;
    const totalSelected = this.getTotalSelectedItems();
    
    const increaseBtn = this.packModal.querySelector('.quantity-increase');
    if (increaseBtn) {
      // Only check inventory quantity if inventory is tracked
      const inventoryLimitReached = inventory.isInventoryTracked && currentQuantity >= inventory.quantity;
      increaseBtn.disabled = totalSelected >= this.maxItems || inventoryLimitReached;
      increaseBtn.style.opacity = increaseBtn.disabled ? '0.5' : '1';
    }
  }

  updateAllAddButtons() {
    const totalSelected = this.getTotalSelectedItems();
    const allAddButtons = this.querySelectorAll('.pack-add-btn');
    const allIncreaseButtons = this.querySelectorAll('.quantity-increase');

    allAddButtons.forEach(btn => {
      const productId = btn.dataset.productId;
      const isSelected = this.selectedProducts.has(productId);
      const inventory = this.getProductInventory(productId);

      if (!inventory.available) {
        btn.disabled = true;
        btn.textContent = 'Sold Out';
        btn.style.background = DISABLED_BG;
        btn.style.color = DISABLED_TEXT;
      } else if (totalSelected >= this.maxItems && !isSelected) {
        btn.disabled = true;
        btn.textContent = 'Pack Full';
        btn.style.background = DISABLED_BG;
        btn.style.color = DISABLED_TEXT;
      } else if (!isSelected) {
        btn.disabled = false;
        const product = this.getProductData(productId);
        btn.textContent = `Add • $${product?.price || '0.00'}`;
        btn.style.background = COLOR_PRIMARY;
        btn.style.color = 'white';
      }
    });

    allIncreaseButtons.forEach(btn => {
      const productId = btn.closest('.pack-item')?.dataset.productId;
      const inventory = this.getProductInventory(productId);
      const currentQuantity = this.selectedProducts.get(productId)?.[0] || 0;
      
      // Only check inventory quantity if inventory is tracked
      const inventoryLimitReached = inventory.isInventoryTracked && currentQuantity >= inventory.quantity;
      btn.disabled = totalSelected >= this.maxItems || inventoryLimitReached;
      if (btn.disabled) {
        btn.style.opacity = '0.5';
      } else {
        btn.style.opacity = '1';
      }
    });
  }

  selectFrequency(option) {
    this.frequencyOptions.forEach(opt => opt.classList.remove('selected'));
    option.classList.add('selected');
    this.selectedFrequency = option.dataset.frequency;
    this.updateDisplay();
  }

  toggleDietaryFilter(filter) {
    filter.classList.toggle('active');
    this.filterProducts();
  }

  filterProducts() {
    const activeFilters = Array.from(this.querySelectorAll('.dietary-filter.active'))
      .map(filter => filter.dataset.filter);

    const products = this.querySelectorAll('.pack-item');
    products.forEach(product => {
      const productFilters = product.dataset.filters?.split(',') || [];
      const shouldShow = activeFilters.length === 0 ||
        activeFilters.every(filter => productFilters.includes(filter));

      product.style.display = shouldShow ? 'block' : 'none';
    });
  }

  validateDeliveryZone() {
    if (!this.zipCodeInput) return;

    const zipCode = this.zipCodeInput.value.trim();
    if (!zipCode) {
      this.updateDeliveryStatus('', 'neutral');
      return;
    }

    const isValid = this.deliveryZones.length === 0 || this.deliveryZones.includes(zipCode);

    if (isValid) {
      this.updateDeliveryStatus('✓ We deliver to your area!', 'success');
    } else {
      this.updateDeliveryStatus('Sorry, we don\'t currently deliver to this zip code.', 'error');
    }
  }

  updateDeliveryStatus(message, type) {
    if (!this.deliveryStatus) return;

    this.deliveryStatus.textContent = message;
    this.deliveryStatus.className = `delivery-status delivery-status--${type}`;
  }

  getTotalSelectedItems() {
    return Array.from(this.selectedProducts.values()).reduce((sum, qty) => sum + qty[0], 0);
  }

  clearExcessProducts() {
    let totalItems = this.getTotalSelectedItems();
    const entries = Array.from(this.selectedProducts.entries());

    for (const [productId, [quantity, selling_plane]] of entries) {
      if (totalItems <= this.maxItems) break;

      const excessQuantity = Math.min(quantity, totalItems - this.maxItems);
      const newQuantity = quantity - excessQuantity;

      if (newQuantity > 0) {
        this.selectedProducts.set(productId, [newQuantity, selling_plane]);
      } else {
        this.selectedProducts.delete(productId);
      }

      totalItems -= excessQuantity;
      this.updateProductDisplay(productId);
    }
  }

  updateDisplay() {
    this.updateProgressBar();
    this.updateStepIndicators();
    this.updateSelectedCount();
    this.updatePackSummary();
    this.updateAddToCartButton();
    this.updatePackPreview();
    this.updateAllAddButtons();
    this.updatePackSizeSelector();
    this.updateDeliveryFrequencySelector();
  }

  updateProgressBar() {
    if (!this.progressBar) return;

    const totalSteps = 4;
    let progress = 0;

    if (this.selectedPackSize) progress += 25;
    if (this.getTotalSelectedItems() > 0) progress += 25;
    if (this.getTotalSelectedItems() === this.maxItems) progress += 25;
    if (this.selectedFrequency) progress += 25;

    this.progressBar.style.width = `${progress}%`;
  }

  updateStepIndicators() {
    this.stepIndicators?.forEach((indicator, index) => {
      const stepNumber = index + 1;
      indicator.classList.remove('active', 'completed');

      if (stepNumber === this.currentStep) {
        indicator.classList.add('active');
      } else if (stepNumber < this.currentStep) {
        indicator.classList.add('completed');
      }
    });
  }

  updateSelectedCount() {
    const totalSelected = this.getTotalSelectedItems();
    const previousCount = parseInt(this.querySelector('.selected-count')?.textContent || '0');

    const selectedCountElements = this.querySelectorAll('.selected-count');
    selectedCountElements.forEach(element => {
      element.textContent = totalSelected;
    });

    const maxItemsElements = this.querySelectorAll('.max-items');
    maxItemsElements.forEach(element => {
      element.textContent = this.maxItems;
    });

    const packSelectionCount = this.querySelector('.pack-selection-count');
    if (packSelectionCount) {
      packSelectionCount.innerHTML = `<span class="selected-count">${totalSelected}</span>/<span class="max-items">${this.maxItems}</span> Selected`;
    }

    if (totalSelected !== previousCount) {
      this.animateCountChange();
    }
  }

  animateCountChange() {
    const selectedCountElements = this.querySelectorAll('.selected-count');
    selectedCountElements.forEach(element => {
      element.classList.add('updating');
      setTimeout(() => {
        element.classList.remove('updating');
      }, 400);
    });
  }

  updatePackSummary() {
    if (!this.packSummary) return;

    const selectedItemsCount = this.getTotalSelectedItems();
    let currentPackPrice = 0;

    if (selectedItemsCount > 0) {
      for (const [productId, [quantity, selling_plane]] of this.selectedProducts.entries()) {
        const productData = this.getProductData(productId);
        if (productData) {
          currentPackPrice += (productData.price * quantity);
        }
      }
    }

    const totalPrice = currentPackPrice;
    const perItemPrice = selectedItemsCount > 0 ? (currentPackPrice / selectedItemsCount).toFixed(2) : '0.00';

    const summary = {
      packSize: this.selectedPackSize,
      packPrice: currentPackPrice.toFixed(2),
      totalPrice: totalPrice.toFixed(2),
      perItemPrice: perItemPrice,
      selectedItems: selectedItemsCount,
      frequency: this.selectedFrequency
    };

    this.dispatchEvent(new CustomEvent('pack-summary-updated', {
      detail: summary,
      bubbles: true
    }));

    const packPriceElement = this.querySelector('.pack-price');
    const totalPriceElement = this.querySelector('.total-price');
    const perItemPriceSpan = this.querySelector('.per-item-price');

    if (packPriceElement) {
      packPriceElement.textContent = `$${currentPackPrice.toFixed(2)}`;
    }

    if (totalPriceElement) {
      totalPriceElement.textContent = `$${totalPrice.toFixed(2)}`;
    }

    if (perItemPriceSpan) {
      perItemPriceSpan.textContent = `$${perItemPrice}`;
    }
  }

  updatePackSizeSelector() {
    const packSizeSelector = this.querySelector('.pack-size-selector');
    if (packSizeSelector) {
      packSizeSelector.value = this.selectedPackSize;
    }
  }

  updateDeliveryFrequencySelector() {
    const deliveryFrequencySelector = this.querySelector('.delivery-frequency-selector');
    if (deliveryFrequencySelector) {
      deliveryFrequencySelector.value = this.selectedFrequency;
    }

    const selectedFrequencyElement = this.querySelector('.selected-frequency');
    if (selectedFrequencyElement) {
      selectedFrequencyElement.textContent = this.selectedFrequency.charAt(0).toUpperCase() + this.selectedFrequency.slice(1);
    }
  }

  updateAddToCartButton() {
    if (!this.addToCartBtn) return;

    const canAddToCart = this.getTotalSelectedItems() === this.maxItems && this.selectedFrequency;

    this.addToCartBtn.disabled = !canAddToCart;
    this.addToCartBtn.textContent = canAddToCart ?
      'Add Pack to Cart' :
      `Select ${this.maxItems - this.getTotalSelectedItems()} more items`;
  }

  nextStep() {
    if (this.currentStep < 4) {
      this.currentStep++;
      this.scrollToCurrentStep();
    }
  }

  prevStep() {
    if (this.currentStep > 1) {
      this.currentStep--;
      this.scrollToCurrentStep();
    }
  }

  scrollToCurrentStep() {
    const stepElement = this.querySelector(`[data-step="${this.currentStep}"]`);
    if (stepElement) {
      stepElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  async clearCart() {
    try {
      await fetch('/cart/clear.js', { method: 'POST', headers: { 'Content-Type': 'application/json' } });
    } catch (error) {
      console.error('Error clearing cart:', error);
    }    
  }

  async addPackToCart() {
    if (this.getTotalSelectedItems() !== this.maxItems) {
      this.showNotification('Please select all items for your pack.', 'error');
      return;
    }

    // Validate inventory for all selected products
    for (const [productId, [quantity, selling_plane]] of this.selectedProducts.entries()) {
      const inventory = this.getProductInventory(productId);
      if (!inventory.available) {
        this.showNotification('One or more products are no longer available.', 'error');
        return;
      }
      // Only check quantity limits if inventory is tracked
      if (inventory.isInventoryTracked && quantity > inventory.quantity) {
        this.showNotification(`Not enough inventory for one or more products.`, 'error');
        return;
      }
    }

    try {
      this.addToCartBtn.disabled = true;
      this.addToCartBtn.textContent = 'Adding to Cart...';
      const uniqueId = crypto.randomUUID();

      // Clear existing cart before adding the new pack
      await this.clearCart();

      const bundleProducts = Array.from(this.selectedProducts.entries()).map(([productId, [quantity, selling_plane]]) => ({
        id: productId,
        quantity: quantity,
        parent_id: this.parentId,
        selling_plan: selling_plane && selling_plane.length > 0 ? selling_plane[Number(this.selectedFrequency)] : null,
        properties: {
          '_bundle_items': 'true',
          '_bundle_id': uniqueId,
          '_pack_size': this.selectedPackSize,
          '_creator_id': `${this.isInfluencer}`,
        }
      }));
      const cartItems = [
        {
          id: this.parentId,
          quantity: 1,
          properties: bundleProducts[0]?.properties
        },
        ...bundleProducts
      ];
      console.log(cartItems);

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ items: cartItems })
      });
      const res = await response.json();
      console.log(res);

      if (response.ok) {
        this.showNotification('Pack added to cart successfully!', 'success');

        document.dispatchEvent(new CustomEvent('cart:refresh'));

        const cart = await fetch('/cart.js').then(res => res.json());
        const cartCount = cart.items.reduce((total, item) => total + item.quantity, 0);
        const cart_badge_background = document.querySelector('.cart-bubble');
        const cart_badge = document.querySelector('.cart-bubble__text-count');
        cart_badge ? cart_badge.textContent = cartCount : {};
        cart_badge?.classList.remove('hidden');
        cart_badge_background?.classList.remove('visually-hidden');
        window.location.assign('/checkout');
      } else {
        throw new Error('Failed to add items to cart');
      }

    } catch (error) {
      console.error('Error adding pack to cart:', error);
      this.showNotification('Error adding pack to cart. Please try again.', 'error');
    } finally {
      // this.addToCartBtn.disabled = false;
      this.updateAddToCartButton();
    }
  }

  showMaxItemsMessage() {
    this.showNotification(`Your ${this.maxItems}-pack is full! Remove an item to add a different one.`, 'warning');
  }

  showNotification(message, type = 'info') {
    const notification = document.createElement('div');
    notification.className = `pack-builder-notification pack-builder-notification--${type}`;
    notification.innerHTML = `
      <span class="notification-message">${message}</span>
      <button class="notification-close" aria-label="Close notification">×</button>
    `;

    document.body.appendChild(notification);

    setTimeout(() => {
      if (notification.parentNode) {
        notification.remove();
      }
    }, 5000);

    notification.querySelector('.notification-close').addEventListener('click', () => {
      notification.remove();
    });

    requestAnimationFrame(() => {
      notification.classList.add('show');
    });
  }

  showNotifications() {
    const hasSavedProgress = localStorage.getItem(this.storageKey);
    if (hasSavedProgress && !this.isInfluencer) {
      this.showNotification('We saved your progress from your last visit. You can edit your pack selections, or keep them as-is.', 'info');
    }

    const packSizeUpdated = localStorage.getItem('pack-size-updated');
    if (packSizeUpdated) {
      this.showNotification('We\'ve recently updated our pack options. Click here to confirm your cup selections and new pack size!', 'info');
      localStorage.removeItem('pack-size-updated');
    }
  }

  saveProgress() {
    const progress = {
      currentStep: this.currentStep,
      selectedPackSize: this.selectedPackSize,
      selectedProducts: Array.from(this.selectedProducts.entries()),
      selectedFrequency: this.selectedFrequency,
      timestamp: Date.now()
    };

    localStorage.setItem(this.storageKey, JSON.stringify(progress));
  }

  loadProgress(savedProduct, savedPackSize) {
    if (this.loading) {
      this.querySelector('.pack-page-loading')?.classList.remove('hidden');
    } else {
      this.querySelector('.pack-page-loading')?.classList.add('hidden');
      try {
        const saved = localStorage.getItem(this.storageKey);

        const progress = JSON.parse(saved) || {};

        const twentyFourHours = 24 * 60 * 60 * 1000;
        if (Date.now() - progress.timestamp > twentyFourHours) {
          localStorage.removeItem(this.storageKey);
          return;
        }

        this.currentStep = progress.currentStep || 1;
        if (savedProduct && savedPackSize) {
          this.selectedPackSize = savedPackSize;
          this.selectedProducts = new Map(savedProduct || []);
        } else {
          this.selectedPackSize = progress.selectedPackSize || 6;
          this.selectedProducts = new Map(progress.selectedProducts || []);
        }
        this.maxItems = this.selectedPackSize;
        this.selectedFrequency = progress.selectedFrequency || '0';

      } catch (error) {
        console.error('Error loading pack builder progress:', error);
        localStorage.removeItem(this.storageKey);
      }
    }
  }

  restoreVisualState() {
    this.selectedProducts.forEach((quantity, productId) => {
      this.updateProductDisplay(productId);
    });

    const packSizeSelector = this.querySelector('.pack-size-selector');
    if (packSizeSelector) {
      packSizeSelector.value = this.selectedPackSize;
    }

    const deliveryFrequencySelector = this.querySelector('.delivery-frequency-selector');
    if (deliveryFrequencySelector) {
      deliveryFrequencySelector.value = this.selectedFrequency;
    }
  }

  getProductData(productId) {
    const productElement = this.querySelector(`[data-product-id="${productId}"]`);
    if (!productElement) return null;

    return {
      id: productId,
      title: productElement.dataset.productTitle || '',
      price: parseFloat(productElement.dataset.productPrice) || 0,
      image: productElement.dataset.productImage || '',
      calories: productElement.dataset.calories || '',
      protein: productElement.dataset.protein || '',
      dietary: productElement.dataset.filters?.split(',') || [],
      quantity: parseInt(productElement.dataset.productQuantity) || 0,
      available: productElement.dataset.productAvailable === 'true'
    };
  }

  getProductInventory(productId) {
    const productElement = this.querySelector(`[data-product-id="${productId}"]`);
    if (!productElement) return { quantity: 0, available: false };

    const inventoryManagement = productElement.dataset.productInventoryManagement;
    const inventoryPolicy = productElement.dataset.productInventoryPolicy;
    const quantity = parseInt(productElement.dataset.productQuantity) || 0;
    const available = productElement.dataset.productAvailable === 'true';

    // If inventory management is null/blank (don't track inventory), always allow
    const isInventoryTracked = inventoryManagement && inventoryManagement !== 'null' && inventoryManagement !== '';
    
    return {
      quantity: quantity,
      available: isInventoryTracked ? available : true, // Always available if not tracking inventory
      isInventoryTracked: isInventoryTracked,
      inventoryPolicy: inventoryPolicy
    };
  }

  handlePackOverflow() {
    const overflowMessage = `Note: if you want more cups than what can fit into a ${this.selectedPackSize}-pack, you can always build another one! Just visit Build Your Pack and go through checkout to set up an additional autoshipment.`;
    this.showNotification(overflowMessage, 'info');
  }

  checkDeliveryZone(zipCode) {
    if (!zipCode || this.deliveryZones.length === 0) return true;
    return this.deliveryZones.includes(zipCode);
  }

  calculateLoyaltyPoints() {
    const basePoints = Math.floor(this.packPrices[this.selectedPackSize] * 0.03);
    return basePoints;
  }

  announceToScreenReader(message) {
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', 'polite');
    announcement.setAttribute('aria-atomic', 'true');
    announcement.className = 'sr-only';
    announcement.textContent = message;

    document.body.appendChild(announcement);

    setTimeout(() => {
      document.body.removeChild(announcement);
    }, 1000);
  }

  handleKeyboardNavigation(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      const target = event.target;

      if (target.classList.contains('pack-option')) {
        this.selectPackSize(target);
        event.preventDefault();
      } else if (target.classList.contains('pack-item')) {
        this.toggleProductSelection(target.dataset.productId);
        event.preventDefault();
      } else if (target.classList.contains('frequency-option')) {
        this.selectFrequency(target);
        event.preventDefault();
      }
    }
  }
}

if (!customElements.get('pack-builder-component')) {
  customElements.define('pack-builder-component', PackBuilderComponent);
}

// Notification styles moved to sections/pack-builder.liquid stylesheet